import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import _ from "lodash";
import { CardProps } from "../components/stateManagement/artZustand";

// Helper function to generate the PDF once all preconditions are met
const generatePDF = (input: HTMLElement) => {
  html2canvas(input, {
    width: input.scrollWidth,
    height: input.scrollHeight,
    scale: 1, // Adjust scale if necessary
    useCORS: true, // Helps with cross-origin images, not needed for base64 but good practice
    logging: true, // Enable for debugging purposes
  }).then((canvas) => {
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    const contentDataURL = canvas.toDataURL("image/png");
    const pdf = new jsPDF('p', 'mm', 'a4');
    let position = 0;

    // Add the first captured image
    pdf.addImage(contentDataURL, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Add new pages if the content spills over
    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(contentDataURL, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save('download.pdf');
  }).catch(err => {
    console.error("Error exporting PDF: ", err);
  });
};

// Main function to export the PDF, ensuring all images are loaded before capture
export const exportPDF = (id = "mainContent") => {
  //scrollToRight();
  //scrollToBottom();
  
  const input = document.getElementById(id);
  
  if (input) {
    const images = input.querySelectorAll('img');
    const imageLoadPromises = [];

    for (const img of images) {
      // Wrap the load event in a promise
      const loadPromise = new Promise((resolve, reject) => {
        img.onload = () => resolve(img);
        img.onerror = reject;
        // For base64 images, setting src again won't trigger CORS issues and forces a reload, ensuring onload fires
        img.src = img.src;
      });
      imageLoadPromises.push(loadPromise);
    }

    // Wait for all images to load
    Promise.all(imageLoadPromises).then(() => {
      // Proceed with a slight delay to ensure rendering settles
      setTimeout(() => generatePDF(input), 500);
    }).catch(error => {
      console.error("An error occurred loading an image: ", error);
    });

  } else {
    console.error("Element to export not found");
  }
};

export const scrollToRight = async (mainDivRef: React.RefObject<HTMLIonContentElement>) => {
  const scrollElement = await mainDivRef.current?.getScrollElement();
  // Smooth scroll to right
  scrollElement?.scrollTo({
    left: scrollElement.scrollWidth,
    behavior: 'smooth'
  });
}

export const scrollToBottom = async (mainDivRef: React.RefObject<HTMLIonContentElement>) => {
  const scrollElement = await mainDivRef.current?.getScrollElement();
  // Smooth scroll to bottom
  scrollElement?.scrollTo({
    top: scrollElement.scrollHeight,
    behavior: 'smooth'
  });
}

export const scrollToTop = async (mainDivRef: React.RefObject<HTMLIonContentElement>) => {
  const scrollElement = await mainDivRef.current?.getScrollElement();
  // Smooth scroll to top
  scrollElement?.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
}

export const scrollToLeft = async (mainDivRef: React.RefObject<HTMLIonContentElement>) => {
  const scrollElement = await mainDivRef.current?.getScrollElement();
  // Smooth scroll to left
  scrollElement?.scrollTo({
    left: 0,
    behavior: 'smooth'
  });
}

export const scrollToBottomThenTop = async (contentRef:  React.RefObject<HTMLIonContentElement>) => {
  const scrollElement = await contentRef.current?.getScrollElement();
  if (!scrollElement) return;

  // Smooth scroll to bottom
  await new Promise<void>((resolve) => {
    const totalScrollDistance = scrollElement.scrollHeight - scrollElement.clientHeight;
    const duration = 1000; // Duration in milliseconds
    let startTime: number | null = null;

    const scroll = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsedTime = timestamp - startTime;
      const progress = Math.min(elapsedTime / duration, 1);

      scrollElement.scrollTo(0, totalScrollDistance * progress);

      if (progress < 1) {
        requestAnimationFrame(scroll);
      } else {
        resolve();
      }
    };

    requestAnimationFrame(scroll);
  });

  // Wait a bit before scrolling back up
  await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for 1000 milliseconds

  // Smooth scroll back to top
  scrollElement.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

export interface CardBoundingRect {
  width: number;
  height: number;
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}

export interface PositionedCard extends CardBoundingRect {
  left: number;
  top: number;
}

export const arrangeCardsInRows = (cardsBoundingRects: CardBoundingRect[], maxWidth: number, spacing: number): PositionedCard[] => {
  let positionedCards: PositionedCard[] = [];
  let currentRowTop = 0;
  let currentRowWidth = 0;
  let maxHeightInRow = 0;

  cardsBoundingRects.forEach((cardRect, index) => {
    // Check if adding the current card exceeds maxWidth, considering the spacing for all but the first card in a row
    if ((currentRowWidth + (index > 0 ? spacing : 0) + cardRect.width) > maxWidth) {
      // Start a new row
      currentRowTop += maxHeightInRow + spacing; // Also add spacing below each row, if you want spacing between rows
      currentRowWidth = 0;
      maxHeightInRow = 0;
    }

    // Position the current card, adjusting for spacing if it's not the first card
    positionedCards.push({
      ...cardRect,
      left: currentRowWidth + (currentRowWidth > 0 ? spacing : 0), // Add spacing before the card if it's not the first one in a row
      top: currentRowTop,
    });

    // Update the row width and the tallest card height in the current row
    // Include the spacing in the row width if it's not the first card
    currentRowWidth += cardRect.width + (currentRowWidth > 0 ? spacing : 0);
    maxHeightInRow = Math.max(maxHeightInRow, cardRect.height);
  });

  return positionedCards;
};

export const gatherCardsBoundingRects = (
  cardRefs: React.MutableRefObject<Array<React.RefObject<HTMLDivElement>>>
): CardBoundingRect[] => {
  return cardRefs.current.map(ref => {
    if (ref.current !== null) {
      const rect = ref.current.getBoundingClientRect();
      // Assuming CardBoundingRect is correctly defined to match getBoundingClientRect
      return {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left,
        right: rect.right,
        bottom: rect.bottom,
      };
    }
    return { width: 0, height: 0 };
  });
};

export const resetCardsInRows = (
  cardsStates: CardProps[],
  mainContentDivRef: React.RefObject<HTMLDivElement>,
  cardRefs: React.MutableRefObject<Array<React.RefObject<HTMLDivElement>>>) => {
  const cardsBR = gatherCardsBoundingRects(cardRefs);
  // Get width of "mainContent" div
  const newCardsBR = arrangeCardsInRows(cardsBR,mainContentDivRef.current!.getBoundingClientRect().width, 5);
  // Update cardsStates with new positions
  let newCardsStates = _.cloneDeep(cardsStates);
  newCardsBR.forEach((card, index) => {
    newCardsStates[index].x = card.left;
    newCardsStates[index].y = card.top;
  });
  
  return newCardsStates;
}